import React, { useCallback, useEffect, useState } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, Plus, Search } from 'lucide-react';
import { LoadingBlock } from '@/components/ui/spinner';
import { parseServerTime } from '@/lib/serverTime';
import {
  marketingSendLogApi,
  marketingTargetSiteApi,
  MarketingEmailSendLog,
  MarketingManualCandidateResult,
  MarketingManualCandidateStatus,
  MarketingSendResult,
} from '@/api/marketingOutreachApi';

// ── 마케팅 아웃리치(홍보 이메일) 발송 현황 (2026-09-13 신설) ────────────────
// 일자/사이트구분/사이트/이메일/발송결과로 필터링 가능한 발송 이력 조회 화면.

const RESULT_LABELS: Record<MarketingSendResult, string> = {
  SUCCESS: '성공',
  FAILED: '실패',
};

const RESULT_COLORS: Record<MarketingSendResult, string> = {
  SUCCESS: 'bg-green-100 text-green-700 border-green-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200',
};

// ── 후보 사이트 수동 등록 (무상 경로, 2026-09-14 신설) ──────────────────────
// Anthropic 유료 웹검색 대신, 관리자가 후보 홈페이지 URL을 직접 붙여넣으면
// 서버가 접속해 회사명/이메일만 추출해 발송 대상으로 등록한다.

const CANDIDATE_CATEGORIES = [
  '온라인 쇼핑몰(자사몰/브랜드몰)',
  '국가기관/공공기관',
  '웹접근성 컨설팅/인증기관',
  '쇼핑몰 구축 대행사/에이전시',
  'SEO/마케팅 에이전시',
  '웹 유지보수/운영대행사',
  'UI/UX 디자인 스튜디오',
];

const CANDIDATE_STATUS_LABELS: Record<MarketingManualCandidateStatus, string> = {
  PENDING: '등록됨(발송대기)',
  EXCLUDED: '제외됨',
  DUPLICATE: '중복',
  ERROR: '오류',
};

const CANDIDATE_STATUS_COLORS: Record<MarketingManualCandidateStatus, string> = {
  PENDING: 'bg-green-100 text-green-700 border-green-200',
  EXCLUDED: 'bg-gray-100 text-gray-600 border-gray-200',
  DUPLICATE: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  ERROR: 'bg-red-100 text-red-700 border-red-200',
};

export default function AdminMarketingSendLogs() {
  const [logs, setLogs] = useState<MarketingEmailSendLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [category, setCategory] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<'' | MarketingSendResult>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [candidateUrls, setCandidateUrls] = useState('');
  const [candidateCategory, setCandidateCategory] = useState(CANDIDATE_CATEGORIES[0]);
  const [addingCandidates, setAddingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [candidateResults, setCandidateResults] = useState<MarketingManualCandidateResult[] | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingSendLogApi.search({
        page,
        size,
        category: category.trim() || undefined,
        companyName: companyName.trim() || undefined,
        email: email.trim() || undefined,
        result: result || undefined,
        startDate: startDate ? `${startDate}T00:00:00` : undefined,
        endDate: endDate ? `${endDate}T23:59:59` : undefined,
      });
      const data = res.data;
      setLogs(data.content ?? []);
      setTotalElements(data.totalElements ?? 0);
      setTotalPages(data.totalPages ?? 0);
    } catch {
      setError('발송 현황을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => {
    setPage(0);
    // page가 이미 0이면 useEffect가 재실행 안 되므로 직접 호출
    if (page === 0) fetchLogs();
  };

  const handleReset = () => {
    setCategory('');
    setCompanyName('');
    setEmail('');
    setResult('');
    setStartDate('');
    setEndDate('');
    setPage(0);
    setTimeout(fetchLogs, 0);
  };

  const handleAddCandidates = async () => {
    const urls = candidateUrls
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (urls.length === 0) {
      setCandidateError('홈페이지 URL을 1개 이상 입력해주세요.');
      return;
    }
    setAddingCandidates(true);
    setCandidateError(null);
    try {
      const res = await marketingTargetSiteApi.manualAdd({ urls, category: candidateCategory });
      setCandidateResults(res.data);
      setCandidateUrls('');
    } catch (err: any) {
      setCandidateError(err?.response?.data?.message ?? '후보 등록에 실패했습니다.');
    } finally {
      setAddingCandidates(false);
    }
  };

  const formatDateTime = (iso: string) => {
    const date = parseServerTime(iso);
    return date
      ? date.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      : '-';
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="마케팅 아웃리치 발송 현황"
        description="홍보 이메일 발송 이력을 일자/사이트구분/사이트/이메일/발송결과로 조회합니다."
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>후보 사이트 수동 등록</CardTitle>
          <CardDescription>
            발송 대상으로 삼고 싶은 홈페이지 URL을 한 줄에 하나씩 붙여넣으면, 서버가 접속해 회사명과
            이메일(제휴/협찬/유통 문의, 개인정보보호책임자 등)을 자동으로 추출해 발송 대상으로 등록합니다.
            이메일을 찾지 못하면 발송 대상에서 제외됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {candidateError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{candidateError}</AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-2 block">홈페이지 URL (한 줄에 하나씩)</label>
              <textarea
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                rows={5}
                placeholder={'https://example-shop.com\nhttps://another-site.co.kr'}
                value={candidateUrls}
                onChange={(e) => setCandidateUrls(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">사이트구분(카테고리)</label>
              <select
                value={candidateCategory}
                onChange={(e) => setCandidateCategory(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {CANDIDATE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <Button onClick={handleAddCandidates} disabled={addingCandidates} className="w-full mt-3">
                <Plus className="w-4 h-4 mr-2" />
                {addingCandidates ? '등록 중...' : '후보 등록'}
              </Button>
            </div>
          </div>

          {candidateResults && (
            <div className="mt-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>회사명</TableHead>
                    <TableHead className="text-center">결과</TableHead>
                    <TableHead>메시지</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidateResults.map((r, idx) => (
                    <TableRow key={`${r.url}-${idx}`}>
                      <TableCell className="text-xs text-gray-600 max-w-xs truncate">{r.url}</TableCell>
                      <TableCell className="text-sm">{r.companyName ?? '-'}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${CANDIDATE_STATUS_COLORS[r.status]}`}>
                          {CANDIDATE_STATUS_LABELS[r.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{r.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>검색 필터</CardTitle>
          <CardDescription>사이트구분(카테고리), 사이트명, 이메일, 발송결과, 기간으로 필터링</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Input
              placeholder="사이트구분(카테고리)"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Input
              placeholder="사이트(회사명)"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Input
              placeholder="이메일"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <select
              value={result}
              onChange={(e) => setResult(e.target.value as '' | MarketingSendResult)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">전체 결과</option>
              <option value="SUCCESS">성공</option>
              <option value="FAILED">실패</option>
            </select>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              검색
            </Button>
            <Button onClick={handleReset} variant="outline" disabled={loading}>
              초기화
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>발송 이력</CardTitle>
          <CardDescription>전체 {totalElements.toLocaleString()}건</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>발송일자</TableHead>
                    <TableHead>사이트구분</TableHead>
                    <TableHead>사이트</TableHead>
                    <TableHead>이메일</TableHead>
                    <TableHead className="text-center">발송결과</TableHead>
                    <TableHead>발송사유 / 실패사유</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-16 text-sm">
                        조회된 발송 이력이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : logs.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs whitespace-nowrap text-gray-500">
                        {formatDateTime(entry.sentAt)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{entry.categorySnapshot ?? '-'}</TableCell>
                      <TableCell className="text-sm font-medium">{entry.companyNameSnapshot}</TableCell>
                      <TableCell className="text-xs text-gray-600">{entry.toEmail}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${RESULT_COLORS[entry.result]}`}>
                          {RESULT_LABELS[entry.result]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-md">
                        {entry.result === 'FAILED' ? (entry.failReason ?? '-') : (entry.aiReason ?? '-')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                variant="outline"
              >
                이전
              </Button>
              <span className="px-4 py-2 text-sm">
                {page + 1} / {totalPages}
              </span>
              <Button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                variant="outline"
              >
                다음
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
